import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Skeleton,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Visibility,
  Error,
  Verified,
  Warning,
  Image,
  PictureAsPdf,
} from '@mui/icons-material';
import { DocumentService, DocumentPreview } from '@/utils/document-service';

interface DocumentThumbnailProps {
  documentId: string;
  documentType: 'id_front' | 'id_back';
  onView: (documentId: string, documentType: 'id_front' | 'id_back') => void;
  verified?: boolean;
  size?: 'small' | 'medium' | 'large';
}

export const DocumentThumbnail: React.FC<DocumentThumbnailProps> = ({
  documentId,
  documentType,
  onView,
  verified = false,
  size = 'medium',
}) => {
  console.log(`[DocumentThumbnail] Component initialized with:`, { documentId, documentType, verified, size });
  
  const [preview, setPreview] = useState<DocumentPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);

  const sizeConfig = {
    small: { width: 60, height: 40 },
    medium: { width: 80, height: 60 },
    large: { width: 120, height: 90 },
  };

  const { width, height } = sizeConfig[size];

  const loadPreview = useCallback(async (forceRefresh = false) => {
    console.log(`[DocumentThumbnail] Loading preview for documentId: ${documentId}, forceRefresh: ${forceRefresh}`);
    setLoading(true);
    setError(null);
    setImageError(false);

    try {
      const previewData = await DocumentService.getDocumentPreview(documentId, forceRefresh);
      console.log(`[DocumentThumbnail] Preview data received:`, previewData);
      setPreview(previewData);
    } catch (err) {
      console.error(`[DocumentThumbnail] Error loading preview for ${documentId}:`, err);
      setError((err as Error)?.message || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    if (!documentId) {
      console.error(`[DocumentThumbnail] No documentId provided for ${documentType}`);
      setError('No document ID provided');
      setLoading(false);
      return;
    }
    
    if (typeof documentId !== 'string' || documentId.trim() === '') {
      console.error(`[DocumentThumbnail] Invalid documentId for ${documentType}:`, documentId);
      setError('Invalid document ID');
      setLoading(false);
      return;
    }
    
    loadPreview();
  }, [documentId, documentType, loadPreview]);

  // Check if URL is expired and refresh proactively
  useEffect(() => {
    if (preview?.thumbnailUrl && DocumentService.isUrlExpired(preview.thumbnailUrl)) {
      console.log('Document URL is expired, refreshing...');
      loadPreview();
    }
  }, [preview?.thumbnailUrl, loadPreview]);

  const handleView = () => {
    onView(documentId, documentType);
  };

  const getDocumentTypeLabel = () => {
    return documentType === 'id_front' ? 'Front' : 'Back';
  };

  const getFileIcon = () => {
    if (!preview) return <Image />;

    const fileName = preview.fileName.toLowerCase();
    if (fileName.endsWith('.pdf')) {
      return <PictureAsPdf />;
    }
    return <Image />;
  };

  if (loading) {
    return (
      <Paper
        variant="outlined"
        sx={{
          width,
          height,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <Skeleton variant="rectangular" width="100%" height="100%" />
      </Paper>
    );
  }

  if (error || !preview) {
    console.log(`[DocumentThumbnail] Showing error state for ${documentId}:`, { error, preview });
    return (
      <Paper
        variant="outlined"
        sx={{
          width,
          height,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'error.light',
          color: 'error.contrastText',
          position: 'relative',
        }}
        title={`Error: ${error || 'No preview data'}`}
      >
        <Error fontSize="small" />
        <Typography variant="caption" align="center" sx={{ mt: 0.5 }}>
          Error
        </Typography>

        {/* Document type label */}
        <Chip
          label={getDocumentTypeLabel()}
          size="small"
          sx={{
            position: 'absolute',
            top: 2,
            left: 2,
            fontSize: '0.6rem',
            height: 16,
          }}
        />
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        width,
        height,
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: 2,
          transform: 'scale(1.02)',
        },
      }}
      onClick={handleView}
    >
      {/* Document Image/Preview */}
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
        }}
      >
        {preview.thumbnailUrl && !imageError ? (
          <img
            src={preview.thumbnailUrl}
            alt={`${documentType} preview`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            onError={(e) => {
              console.error(`[DocumentThumbnail] Image failed to load for ${documentId}:`, {
                url: preview.thumbnailUrl,
                error: e,
                imageError: imageError
              });

              // Try to refresh the URL once if it failed
              if (!imageError) {
                console.log(`[DocumentThumbnail] Attempting to refresh expired document URL for ${documentId}...`);
                setImageError(true); // Temporarily set to prevent infinite loops
                loadPreview(true).catch((refreshError) => {
                  console.error(`[DocumentThumbnail] Failed to refresh document preview for ${documentId}:`, refreshError);
                  setImageError(true);
                });
              } else {
                console.log(`[DocumentThumbnail] Image error already occurred, not retrying for ${documentId}`);
                setImageError(true);
              }
            }}
          />
        ) : (
          <Box sx={{ color: 'grey.500', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {getFileIcon()}
          </Box>
        )}
      </Box>

      {/* Document type label */}
      <Chip
        label={getDocumentTypeLabel()}
        size="small"
        sx={{
          position: 'absolute',
          top: 2,
          left: 2,
          fontSize: '0.6rem',
          height: 16,
          bgcolor: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
        }}
      />

      {/* Verification status */}
      <Box
        sx={{
          position: 'absolute',
          top: 2,
          right: 2,
        }}
      >
        <Tooltip title={verified ? 'Verified' : 'Unverified'}>
          {verified ? (
            <Verified
              fontSize="small"
              sx={{ color: 'success.main', bgcolor: 'white', borderRadius: '50%' }}
            />
          ) : (
            <Warning
              fontSize="small"
              sx={{ color: 'warning.main', bgcolor: 'white', borderRadius: '50%' }}
            />
          )}
        </Tooltip>
      </Box>

      {/* View overlay on hover */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0,
          transition: 'opacity 0.2s ease-in-out',
          '&:hover': {
            opacity: 1,
          },
        }}
      >
        <IconButton
          size="small"
          sx={{ color: 'white' }}
          onClick={(e) => {
            e.stopPropagation();
            handleView();
          }}
        >
          <Visibility />
        </IconButton>
      </Box>

      {/* Upload date */}
      {size !== 'small' && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            p: 0.5,
          }}
        >
          <Typography variant="caption" fontSize="0.6rem">
            {new Date(preview.uploadedAt).toLocaleDateString()}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default DocumentThumbnail;